import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Grid,
  Typography,
  Divider,
  alpha,
  LinearProgress,
  Stack,
  Alert,
  Chip,
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
} from "@mui/material";
import {
  Save,
  CheckCircle,
  Error as ErrorIcon,
  Warning,
  Refresh,
  Add,
  Edit,
  Business,
  Language,
  Description,
  Person,
} from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import MDBox from "shared/components/MDBox";

// Expresiones regulares para validaciones
const NAME_REGEX = /^[A-Za-záéíóúÁÉÍÓÚñÑ\s]+$/;
// Validación de IP más flexible - solo verifica que tenga http:// o https://
const IP_REGEX = /^https?:\/\/.+/;
const IDCOPROPIETARIE_REGEX = /^\d+$/;

// Función para formatear nombre: reemplazar espacios por guiones bajos
const formatNameForApi = (name) => {
  if (!name) return "";
  return String(name).trim().replace(/\s+/g, "_");
};

// Función para mostrar nombre con espacios (inverso del formato)
const formatNameForDisplay = (name) => {
  if (!name) return "";
  return String(name).replace(/_/g, " ");
};

const PropiedadDialog = ({
  open,
  onClose,
  onConfirm,
  dialogType,
  selectedPropiedad,
  currentUser,
  isSubmitting,
  submitStatus,
  statusMessage,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    ip: "",
    description: "",
    idcopropietarie: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [originalName, setOriginalName] = useState("");

  // Resetear formulario cuando se abre el diálogo
  useEffect(() => {
    if (open) {
      if (dialogType === "add") {
        setFormData({
          name: "",
          ip: "",
          description: "",
          idcopropietarie: "",
        });
        setOriginalName("");
        setErrors({});
        setTouched({});
      } else if (dialogType === "edit" && selectedPropiedad) {
        // Para edición, mostrar el nombre con espacios (formato legible)
        const displayName = formatNameForDisplay(selectedPropiedad.name || "");
        setOriginalName(selectedPropiedad.name || "");
        setFormData({
          name: displayName,
          ip: selectedPropiedad.ip || "",
          description: selectedPropiedad.description || "",
          idcopropietarie: selectedPropiedad.idcopropietarie?.toString() || "",
        });
        setErrors({});
        setTouched({});
      }
    }
  }, [open, dialogType, selectedPropiedad]);

  // Validaciones
  const validateName = (value) => {
    const strValue = String(value || "");
    if (!strValue) return "El nombre es requerido";
    if (!NAME_REGEX.test(strValue)) {
      return "El nombre solo debe contener letras y espacios";
    }
    if (strValue.length < 3) return "El nombre debe tener al menos 3 caracteres";
    return "";
  };

  const validateIP = (value) => {
    const strValue = String(value || "");
    if (!strValue) return "La IP es requerida";
    if (!IP_REGEX.test(strValue)) {
      return "Formato de IP inválido. Ej: http://192.168.1.1:3000 o https://dominio.com";
    }
    return "";
  };

  const validateIdCopropietarie = (value) => {
    const strValue = String(value || "");
    if (!strValue) return "El ID del copropietario es requerido";
    if (!IDCOPROPIETARIE_REGEX.test(strValue)) {
      return "El ID debe contener solo números";
    }
    return "";
  };

  const validateField = (field, value) => {
    let error = "";

    switch (field) {
      case "name":
        error = validateName(value);
        break;
      case "ip":
        error = validateIP(value);
        break;
      case "idcopropietarie":
        error = validateIdCopropietarie(value);
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  const isFieldError = (field) => {
    return touched[field] && errors[field];
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    validateField(name, value);
  };

  const validateForm = () => {
    const fields = ["name", "ip", "idcopropietarie"];
    let isValid = true;

    fields.forEach((field) => {
      const fieldValid = validateField(field, formData[field]);
      if (!fieldValid) isValid = false;
      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }));
    });

    return isValid;
  };

  const handleConfirm = () => {
    if (!validateForm()) {
      return;
    }

    // Preparar datos para enviar
    const dataToSend = {
      name: formatNameForApi(formData.name),
      ip: String(formData.ip || "").trim(),
      description: String(formData.description || "").trim(),
      idcopropietarie: String(formData.idcopropietarie || "").trim(),
    };

    onConfirm(dataToSend);
  };

  // Función para verificar si un campo está completo
  const isFieldComplete = (field) => {
    const value = formData[field];
    if (value === undefined || value === null) return false;
    return String(value).trim() !== "";
  };

  const isFormComplete = () => {
    const requiredFields = ["name", "ip", "idcopropietarie"];
    return requiredFields.every((field) => isFieldComplete(field));
  };

  // Contar campos completos
  const getCompletedFieldsCount = () => {
    const fields = ["name", "ip", "description", "idcopropietarie"];
    return fields.filter((field) => isFieldComplete(field)).length;
  };

  // Renderizar contenido según el tipo de diálogo
  const renderContent = () => {
    // 🔴 Determinar si es modo edición
    const isEditMode = dialogType === "edit";

    return (
      <Stack spacing={3}>
        {submitStatus && (
          <Alert
            severity={submitStatus === "success" ? "success" : "error"}
            sx={{ borderRadius: "8px" }}
          >
            <Typography variant="body2" fontWeight="medium">
              {statusMessage}
            </Typography>
          </Alert>
        )}

        <MDBox
          sx={{
            p: 3,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
          }}
        >
          <Grid container spacing={3}>
            {/* Nombre */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre *"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                error={isFieldError("name")}
                helperText={isFieldError("name") && errors.name}
                disabled={isSubmitting}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business sx={{ color: "#5c6bc0" }} />
                    </InputAdornment>
                  ),
                }}
                placeholder="Ej: Edificio Central"
                sx={{
                  "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                El nombre se guardará como:{" "}
                <strong>{formatNameForApi(formData.name) || "..."}</strong>
              </Typography>
            </Grid>

            {/* IP */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IP *"
                name="ip"
                value={formData.ip}
                onChange={handleFormChange}
                error={isFieldError("ip")}
                helperText={isFieldError("ip") && errors.ip}
                disabled={isSubmitting}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Language sx={{ color: "#5c6bc0" }} />
                    </InputAdornment>
                  ),
                }}
                placeholder="Ej: http://192.168.1.1:3000"
                sx={{
                  "& .MuiInputBase-root": { height: "56px", fontSize: "1rem" },
                }}
              />
            </Grid>

            {/* ID Copropietario - 🔴 DESHABILITADO EN MODO EDICIÓN */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="ID Copropietario *"
                name="idcopropietarie"
                value={formData.idcopropietarie}
                onChange={handleFormChange}
                error={isFieldError("idcopropietarie")}
                helperText={
                  isEditMode
                    ? "El ID del copropietario no se puede modificar en edición"
                    : isFieldError("idcopropietarie") && errors.idcopropietarie
                }
                disabled={isSubmitting || isEditMode} // 🔴 Deshabilitado en edición
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: isEditMode ? "#9e9e9e" : "#5c6bc0" }} />
                    </InputAdornment>
                  ),
                  readOnly: isEditMode, // 🔴 Solo lectura en edición
                }}
                placeholder="Ej: 12345"
                sx={{
                  "& .MuiInputBase-root": {
                    height: "56px",
                    fontSize: "1rem",
                    backgroundColor: isEditMode ? "#f5f5f5" : "transparent", // 🔴 Fondo gris en edición
                  },
                }}
              />
              {isEditMode && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  <Person sx={{ fontSize: "0.8rem", verticalAlign: "middle", mr: 0.5 }} />
                  Este campo no se puede modificar en modo edición
                </Typography>
              )}
            </Grid>

            {/* Descripción */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                disabled={isSubmitting}
                variant="outlined"
                multiline
                rows={3}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ alignSelf: "flex-start", mt: 1.5 }}>
                      <Description sx={{ color: "#5c6bc0" }} />
                    </InputAdornment>
                  ),
                }}
                placeholder="Descripción de la propiedad (opcional)"
                sx={{
                  "& .MuiInputBase-root": { fontSize: "1rem" },
                }}
              />
            </Grid>
          </Grid>
        </MDBox>

        {/* Resumen */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: alpha("#5c6bc0", 0.3),
            backgroundColor: alpha("#5c6bc0", 0.02),
          }}
        >
          <Typography variant="h6" fontWeight="600" color="text.primary" gutterBottom>
            RESUMEN DE LA PROPIEDAD
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                NOMBRE
              </Typography>
              <Typography variant="body1" fontWeight="600">
                {formData.name || "—"}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                Se guardará como: {formatNameForApi(formData.name) || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                IP
              </Typography>
              <Typography variant="body1" fontWeight="600">
                {formData.ip || "—"}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                ID COPROPIETARIO
              </Typography>
              <Typography variant="body1" fontWeight="600">
                {formData.idcopropietarie || "—"}
              </Typography>
              {isEditMode && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                  <Person sx={{ fontSize: "0.7rem", verticalAlign: "middle", mr: 0.3 }} />
                  No modificable en edición
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                DESCRIPCIÓN
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {formData.description || "Sin descripción"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.7rem", fontWeight: 500 }}
              >
                COMPLETITUD DEL FORMULARIO
              </Typography>
              <Typography variant="caption" fontWeight="600" sx={{ fontSize: "0.7rem" }}>
                {getCompletedFieldsCount()}/4 campos
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={(getCompletedFieldsCount() / 4) * 100}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: alpha("#5c6bc0", 0.1),
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#5c6bc0",
                  borderRadius: 3,
                },
              }}
            />
          </Box>

          {isFormComplete() ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "8px",
                backgroundColor: alpha("#4caf50", 0.05),
                border: "1px dashed",
                borderColor: alpha("#4caf50", 0.3),
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CheckCircle sx={{ color: "#4caf50", fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                Todos los campos requeridos están completos.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                p: 1.5,
                borderRadius: "8px",
                backgroundColor: alpha("#ff9800", 0.05),
                border: "1px dashed",
                borderColor: alpha("#ff9800", 0.3),
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Warning sx={{ color: "#ff9800", fontSize: 18 }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                Complete todos los campos requeridos para continuar
              </Typography>
            </Box>
          )}
        </Paper>
      </Stack>
    );
  };

  // Renderizar footer
  const renderFooter = () => {
    if (submitStatus === "success") {
      return (
        <MDButton
          variant="contained"
          color="success"
          onClick={onClose}
          startIcon={<CheckCircle />}
          sx={{ px: 4, minWidth: 150, borderRadius: "8px", height: "48px" }}
        >
          Aceptar
        </MDButton>
      );
    }

    if (submitStatus === "error") {
      return (
        <>
          <MDButton
            variant="outlined"
            color="secondary"
            onClick={onClose}
            sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
          >
            Cerrar
          </MDButton>
          <MDButton
            variant="contained"
            color="warning"
            onClick={() => {
              onClose();
            }}
            startIcon={<Refresh />}
            sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
          >
            Reintentar
          </MDButton>
        </>
      );
    }

    return (
      <>
        <MDButton
          variant="outlined"
          color="secondary"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
        >
          Cancelar
        </MDButton>
        <MDButton
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={isSubmitting || !isFormComplete()}
          startIcon={<Save />}
          sx={{ px: 4, minWidth: 200, borderRadius: "8px", fontWeight: "bold", height: "48px" }}
        >
          {isSubmitting
            ? dialogType === "add"
              ? "Agregando..."
              : "Actualizando..."
            : dialogType === "add"
            ? "AGREGAR PROPIEDAD"
            : "ACTUALIZAR PROPIEDAD"}
        </MDButton>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box display="flex" alignItems="center">
          {isSubmitting ? (
            <CircularProgress size={28} sx={{ mr: 2, color: "primary.main" }} />
          ) : submitStatus === "success" ? (
            <CheckCircle sx={{ mr: 2, fontSize: "1.75rem", color: "success.main" }} />
          ) : submitStatus === "error" ? (
            <ErrorIcon sx={{ mr: 2, fontSize: "1.75rem", color: "error.main" }} />
          ) : dialogType === "add" ? (
            <Add sx={{ mr: 2, fontSize: "1.75rem", color: "primary.main" }} />
          ) : (
            <Edit sx={{ mr: 2, fontSize: "1.75rem", color: "primary.main" }} />
          )}
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {dialogType === "add" ? "Agregar Nueva Propiedad" : "Editar Propiedad"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dialogType === "add"
                ? "Complete los campos para agregar una nueva propiedad"
                : "Actualice los datos de la propiedad"}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3 }}>{renderContent()}</DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, borderTop: 1, borderColor: "divider", gap: 1 }}>
        {renderFooter()}
      </DialogActions>
    </Dialog>
  );
};

PropiedadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  dialogType: PropTypes.string,
  selectedPropiedad: PropTypes.object,
  currentUser: PropTypes.object,
  isSubmitting: PropTypes.bool,
  submitStatus: PropTypes.string,
  statusMessage: PropTypes.string,
};

PropiedadDialog.defaultProps = {
  dialogType: "add",
  selectedPropiedad: null,
  currentUser: null,
  isSubmitting: false,
  submitStatus: null,
  statusMessage: "",
};

export default PropiedadDialog;
